import * as React from 'react';

import { usePostMessageContext, useStrings } from 'src/providers';
import { useCurrencyFormatter } from 'src/hooks/useCurrencyFormatter';
import { IContract } from 'src/types/IContract';
import { OptionInputStyles, OptionInputTypes } from 'src/types/IOptions';

import { FormattedString } from 'src/components/Shared/FormattedString';
import { StyledVerticalOption } from '../Vertical/StyledVertical';
import {
  StyledCollapsableList,
  SeeMoreButton,
  SeeLessButton,
} from './StyledCollapsableList';


interface ICollapsableListProps {
  contracts: IContract[];
  openState: boolean;
  selectedContractId: string | null;
  toggleContract: (contractId: string) => void;
  setOpenState: React.Dispatch<React.SetStateAction<boolean>>;
}

export function CollapsableList({
  contracts,
  openState,
  selectedContractId,
  toggleContract,
  setOpenState,
}: ICollapsableListProps) {
  const formatCurrency = useCurrencyFormatter();
  const { postMessage } = usePostMessageContext();
  const { termLabel } = useStrings();

  const hasOneContract = contracts.length === 1;
  const showMoreOptionsLinkSection = !hasOneContract;
  const showMoreOptionsLink = showMoreOptionsLinkSection && openState === false;

  const inputStyle = OptionInputStyles.CHECKBOX;
  const inputType = OptionInputTypes.CHECKBOX;

  const handleSeeMorePlansClick = () => {
    postMessage('showMoreOptions', {
      ctaType: 'productPage',
      availableContracts: contracts.length,
    });
    setOpenState(true);
  };

  const handleSeeLessPlansClick = () => {
    setOpenState(false);
    postMessage('showFewerOptions', {
      ctaType: 'productPage',
    });
  };

  const maybeRenderVisibilityLink = () => {
    return showMoreOptionsLinkSection
      && (
        <div className="options-section">
          {
            showMoreOptionsLink
              ? <SeeMoreButton 
                data-test="whats-covered"
                className="options-btn"
                onClick={handleSeeMorePlansClick}
                type="button">See more plan options</SeeMoreButton>
              : <SeeLessButton 
                data-test="whats-covered"
                className="options-btn"
                onClick={handleSeeLessPlansClick}
                type="button">See fewer plan options</SeeLessButton>
          } 
        </div>
      );
  };

  const sortContractsForDisplay = (contracts: IContract[]) => {
    if (openState) return contracts;
    const selectedContract = contracts.filter((contract) => contract.id === selectedContractId);
    const filteredContracts = contracts.filter((contract) => contract.id !== selectedContractId);
    return [...selectedContract, ...filteredContracts];
  };

  const trimContractsByOpenState = (contracts: IContract[]) => {
    if (!openState || contracts.length === 1) return [contracts[0]];
    return contracts;
  };

  const contractsToShow = React.useMemo(() => {
    return [sortContractsForDisplay, trimContractsByOpenState].reduce((acc, fn) => fn(acc), contracts);
  }, [contracts, sortContractsForDisplay, trimContractsByOpenState]);
  

  return (
    <StyledCollapsableList>
      <ul
        className="contracts"
        data-test='pdp-contracts-valueprop'
        id='contracts-valueprop'
      >
        {contractsToShow.map((contract) => (
          <StyledVerticalOption
            contractId={contract.id}
            inputStyle={inputStyle}
            inputType={inputType}
            isSelected={selectedContractId === contract.id}
            key={contract.id}
            onChange={toggleContract}
          >
            <FormattedString
              className="text"
              string={termLabel}
              tagName="span"
              values={{
                term: contract.term,
                termSingular: contract.termLabelSingular,
              }}
            />
            <span className="price">
              {formatCurrency(+contract.price)}
            </span>
          </StyledVerticalOption>
        ))}
      </ul>

      {maybeRenderVisibilityLink()}

    </StyledCollapsableList>

  );
}
